import { message } from 'antd';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
// Create an Axios instance
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "https://api.qualifyme.ai";

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 60000 * 5,
});

let isHandling401 = false;

const handleExpiredToken = () => {
  localStorage.removeItem("access-token");
};

// Request interceptor to include Bearer token in headers
apiClient.interceptors.request.use(
  (config: AxiosRequestConfig): any => {
    let token = localStorage.getItem("access-token");
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle status codes
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  (error: any): Promise<AxiosError> => {
    if (error.response) {
      switch (error.response.status) {
        case 401: {
          if (!isHandling401) {
            isHandling401 = true;
            const pathname = window.location.pathname;
            const publicNoRedirectPaths = ["/profile-screening"];
            const isPublicNoRedirect = publicNoRedirectPaths.some((p) => pathname === p);
            const isAuthCheck = error.config?.headers?.['X-Auth-Check'] === 'true';

            // Silently handle 401 on public routes - no notification, no redirect
            if (isPublicNoRedirect) {
              handleExpiredToken();
              setTimeout(() => { isHandling401 = false; }, 3000);
              break;
            }

            // Suppress toast for background auth-check on sign-in page
            if (isAuthCheck && pathname === '/sign-in') {
              handleExpiredToken();
              setTimeout(() => { isHandling401 = false; }, 3000);
              break;
            }

            // Only show notification if NOT on a public route
            let errorMessage =
              "Your access token has expired or is invalid. Please log in again.";
            if (pathname === "/sign-in") {
              errorMessage = "Please check your email and password and try again.";
            }
            message.error(errorMessage);

            handleExpiredToken();
            if (pathname !== "/sign-in") {
              const redirect = pathname + window.location.search;
              window.location.href = `/sign-in?redirect=${encodeURIComponent(redirect)}`;
            } else {
              setTimeout(() => { isHandling401 = false; }, 3000);
            }
          }
          break;
        }
        case 403: {
          console.error(
            "Forbidden, you do not have permission to access this resource."
          );
          // Suppress toast on /user-onboarding — the wizard handles errors inline
          const SELF_HANDLED_PATHS = ['/user-onboarding'];
          const shouldSuppressToast = SELF_HANDLED_PATHS.some(p => window.location.pathname.startsWith(p));
          if (!shouldSuppressToast) {
            message.error(error.response?.data?.message);
          }
          break;
        }
        case 404:
          console.error("Resource not found.");
          break;
        case 500:
          console.error("Internal server error, please try again later.");
          break;
        default:
          console.error(`Unexpected error: ${error.response.status}`);
      }
    } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.error("Request timeout - the server took too long to respond.");
      const isAuthCheck = error.config?.headers?.['X-Auth-Check'] === 'true';
      if (!isAuthCheck) {
        const isLoginPage = window.location.pathname === "/sign-in";
        const errorMessage = isLoginPage
          ? "Connection timeout. Please check your internet connection and try again."
          : "Request timeout. Please try again.";
        message.error(errorMessage);
      }
    } else {
      console.error("Network error or server is not responding.");
      const isAuthCheck = error.config?.headers?.['X-Auth-Check'] === 'true';
      if (!isAuthCheck) {
        const isLoginPage = window.location.pathname === "/sign-in";
        if (isLoginPage) {
          message.error("Unable to connect to the server. Please check your internet connection and try again.");
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
